import {
    CardCvvElement,
    CardMonthElement,
    CardNumberElement,
    CardNumberElementChangeEvent,
    CardYearElement,
    IndividualElementChangeEvent,
    useRecurly,
} from '@recurly/react-recurly';
import { createLogger, serializeError } from 'logging';
import { ChangeEventHandler, FormEvent, JSX, MutableRefObject, useCallback, useRef, useState } from 'react';

import ErrorBox from '../../../new/components/ErrorBox';
import { getCountriesWithAbbreviations } from '../../utils/getCountriesWithAbbreviation';
import { getPostalCodeLabel, getPostalCodePlaceholder, validatePostalCode } from '../../utils/postalCodeValidation';
import ApplePayButton from './ApplePayButton';
import { useCheckoutContext } from './CheckoutContext';
import { useDigitalWallets } from './DigitalWalletsContext';
import GooglePayCheckout from './GooglePayCheckout';
import { GooglePayService } from './GooglePayService';
import PayPalCheckout from './PayPalCheckout';
import ValidatedTextInput from './ValidatedInput';
import { PurchaseProps } from './actions';
import { usePurchasePreview } from './usePurchasePreview';

import './recurlyStyles.css';

const logger = createLogger('BillingForm');

export interface BillingFormProps {
    currency?: string;
    onPurchaseSubmit: (
        purchaseProps: PurchaseProps,
        setErrorMessage: (message: string) => void,
        resetSubmissionState: () => void,
    ) => void;
}

export default function BillingForm({ onPurchaseSubmit, currency = 'USD' }: BillingFormProps): JSX.Element {
    const { planCode, setPostalCode, countryCode, setCountryCode } = useCheckoutContext();
    const { total } = usePurchasePreview();
    const formRef = useRef<HTMLFormElement>(null!);
    const recurly = useRecurly();
    const postalCodeLabel = getPostalCodeLabel(countryCode || 'US');
    const postalCodePlaceholder = getPostalCodePlaceholder(countryCode || 'US');
    const [includeTaxIdentifierType, setIncludeTaxIdentifierType] = useState(false);

    const [formErrorMessage, setFormErrorMessage] = useState('');
    const [firstNameErrorMessage, setFirstNameErrorMessage] = useState('');
    const [lastNameErrorMessage, setLastNameErrorMessage] = useState('');
    const [postalCodeErrorMessage, setPostalCodeErrorMessage] = useState('');
    const [countryErrorMessage, setCountryErrorMessage] = useState('');
    const [paypalErrorMessage, setPaypalErrorMessage] = useState('');
    const [googlePayErrorMessage, setGooglePayErrorMessage] = useState('');
    const [cardNumberErrorMessage, setCardNumberErrorMessage] = useState('');
    const [cardMonthErrorMessage, setCardMonthErrorMessage] = useState('');
    const [cardYearErrorMessage, setCardYearErrorMessage] = useState('');
    const [cardCvvErrorMessage, setCardCvvErrorMessage] = useState('');
    const [cpfErrorMessage, setCpfErrorMessage] = useState('');
    const [vatErrorMessage, setVatErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [applePayErrorMessage, setApplePayErrorMessage] = useState('');
    const { isDigitalWalletsEnabled } = useDigitalWallets();

    const handleFirstNameUpdate = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
        if (event.target.value) {
            setFirstNameErrorMessage('');
        }
    }, []);

    const handleLastNameUpdate = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
        if (event.target.value) {
            setLastNameErrorMessage('');
        }
    }, []);

    const handleCardNumberUpdate = useCallback((state: CardNumberElementChangeEvent) => {
        if (state.valid) {
            setCardNumberErrorMessage('');
        } else if ('empty' in state && state.empty === false) {
            // Only show error for explicitly non-empty invalid input
            setCardNumberErrorMessage('Please enter a valid card number.');
        }
        // Don't set error if empty property is missing (partial state) or if field is empty
    }, []);

    const handleCardMonthUpdate = useCallback((state: IndividualElementChangeEvent) => {
        if (state.valid) {
            setCardMonthErrorMessage('');
        } else if ('empty' in state && state.empty === false) {
            // Only show error for explicitly non-empty invalid input
            setCardMonthErrorMessage('Please select a valid month.');
        }
        // Don't set error if empty property is missing (partial state) or if field is empty
    }, []);

    const handleCardYearUpdate = useCallback((state: IndividualElementChangeEvent) => {
        if (state.valid) {
            setCardYearErrorMessage('');
        } else if ('empty' in state && state.empty === false) {
            // Only show error for explicitly non-empty invalid input
            setCardYearErrorMessage('Please select a valid year.');
        }
        // Don't set error if empty property is missing (partial state) or if field is empty
    }, []);

    const handleCardCvvUpdate = useCallback((state: IndividualElementChangeEvent) => {
        if (state.valid) {
            setCardCvvErrorMessage('');
        } else if ('empty' in state && state.empty === false) {
            // Only show error for explicitly non-empty invalid input
            setCardCvvErrorMessage('Please enter a valid CVV.');
        }
        // Don't set error if empty property is missing (partial state) or if field is empty
    }, []);

    const handleZipCodeUpdate = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
            const value = event.target.value;
            setPostalCode(value);

            // Clear error message if a valid postal code is entered
            if (value) {
                setPostalCodeErrorMessage('');
            }
        },
        [setPostalCode],
    );

    const handleCountryUpdate = useCallback<ChangeEventHandler<HTMLSelectElement>>(
        (event) => {
            const value = event.target.value;
            setCountryCode(value);

            // Clear error message if a valid country is selected
            if (value) {
                setCountryErrorMessage('');
            }
        },
        [setCountryCode],
    );

    const handleCpfUpdate = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
        if (event.target.value) {
            setCpfErrorMessage('');
            setIncludeTaxIdentifierType(true);
        } else {
            setIncludeTaxIdentifierType(false);
        }
    }, []);

    const handleVatUpdate = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
        if (event.target.value) {
            setVatErrorMessage('');
        }
    }, []);

    const resetErrorMessages = () => {
        setFirstNameErrorMessage('');
        setLastNameErrorMessage('');
        setPostalCodeErrorMessage('');
        setCountryErrorMessage('');
        setPaypalErrorMessage('');
        setGooglePayErrorMessage('');
        setCardNumberErrorMessage('');
        setCardMonthErrorMessage('');
        setCardYearErrorMessage('');
        setCardCvvErrorMessage('');
        setCpfErrorMessage('');
        setVatErrorMessage('');
        setApplePayErrorMessage('');
        // Keep touched states - don't reset them on form submit
    };

    const resetSubmissionState = () => {
        setIsSubmitting(false);
    };

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        // Prevent automatic page refresh
        event.preventDefault();

        // Prevent multiple submissions
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        resetErrorMessages();

        const formData = new FormData(event.currentTarget);
        const firstName = formData.get('firstName') as string;
        const lastName = formData.get('lastName') as string;
        const postalCode = formData.get('postalCode') as string;
        const country = formData.get('country') as string;

        // Form validation
        let hasValidationErrors = false;

        if (!firstName) {
            setFirstNameErrorMessage('Please enter your first name.');
            hasValidationErrors = true;
        }
        if (!lastName) {
            setLastNameErrorMessage('Please enter your last name.');
            hasValidationErrors = true;
        }
        const postalCodeValidation = validatePostalCode(postalCode, country || 'US');
        if (!postalCodeValidation.isValid) {
            setPostalCodeErrorMessage(postalCodeValidation.errorMessage || 'Invalid postal code');
            hasValidationErrors = true;
        }
        if (country === '') {
            setCountryErrorMessage('Country is required.');
            hasValidationErrors = true;
        }

        // Basic Recurly field validation (will be enhanced by Recurly.token())
        // Note: Detailed validation is handled by Recurly's token generation
        // This is just for required field checking before token generation
        if (!recurly) {
            setFormErrorMessage('Payment system is not ready. Please try again.');
            hasValidationErrors = true;
        }

        // Early return if basic validation fails
        if (hasValidationErrors) {
            setIsSubmitting(false);
            return;
        }

        // Fetch payment token from Recurly, only if validation passes.
        recurly.token(formRef.current, (err, token) => {
            if (err) {
                // Handle specific Recurly validation errors
                if (err.fields) {
                    if (err.fields.includes('number')) {
                        setCardNumberErrorMessage('Please enter a valid card number.');
                    }
                    if (err.fields.includes('month')) {
                        setCardMonthErrorMessage('Please select a valid month.');
                    }
                    if (err.fields.includes('year')) {
                        setCardYearErrorMessage('Please select a valid year.');
                    }
                    // Check for both 'cvv' and 'cvc' field names
                    if (err.fields.includes('cvv') || err.fields.includes('cvc')) {
                        setCardCvvErrorMessage('Please enter a valid CVV.');
                    }
                }

                setFormErrorMessage(err.message || 'An error occurred while processing your payment.');
                logger.error({ error: serializeError(err) });
                setIsSubmitting(false);
            } else {
                // On success, process the purchase
                onPurchaseSubmit(
                    {
                        planCode,
                        tokenId: token.id,
                        firstName,
                        lastName,
                        currency,
                    },
                    setFormErrorMessage,
                    resetSubmissionState,
                );
                // Note: Don't reset isSubmitting here as the form should remain disabled during purchase processing
            }
        });
    }

    const handleApplePaySuccess = useCallback(
        (data: { tokenId: string }) => {
            if (isSubmitting) {
                return;
            }

            setIsSubmitting(true);
            setApplePayErrorMessage('');
            logger.info({ data }, 'Apple Pay success');

            onPurchaseSubmit(
                {
                    planCode,
                    tokenId: data.tokenId,
                },
                setApplePayErrorMessage,
                resetSubmissionState,
            );
        },
        [isSubmitting, onPurchaseSubmit, planCode],
    );

    const handleApplePayError = useCallback((error: { code: string; message: string; cause?: unknown }) => {
        logger.error({ error }, 'Apple Pay error');
        setApplePayErrorMessage(error.message || 'An error occurred with Apple Pay checkout');
    }, []);

    const handlePayPalSuccess = useCallback(
        (data: { tokenId: string }) => {
            // Prevent multiple submissions
            if (isSubmitting) {
                return;
            }

            setIsSubmitting(true);
            setPaypalErrorMessage('');
            logger.info({ data }, 'PayPal success');

            const purchaseData: PurchaseProps = {
                planCode: planCode,
                tokenId: data.tokenId,
            };

            onPurchaseSubmit(purchaseData, setPaypalErrorMessage, resetSubmissionState);
        },
        [onPurchaseSubmit, planCode, isSubmitting],
    );

    const handlePayPalError = useCallback(
        (error: { code: string; message: string; cause?: unknown }) => {
            logger.error({ error }, 'PayPal error');
            setPaypalErrorMessage(error.message || 'An error occurred with PayPal checkout');
        },
        [setPaypalErrorMessage],
    );

    const handleGooglePaySuccess = useCallback(
        (data: { tokenId: string; firstName?: string; lastName?: string }) => {
            if (isSubmitting) {
                return;
            }

            setIsSubmitting(true);
            setGooglePayErrorMessage('');
            logger.info({ data }, 'Google Pay success');

            const purchaseData = {
                planCode: planCode,
                tokenId: data.tokenId,
                firstName: data.firstName || 'Google', // Use extracted or fallback
                lastName: data.lastName || 'Pay', // Use extracted or fallback
                currency: currency, // Use component prop
            };

            onPurchaseSubmit(purchaseData, setGooglePayErrorMessage, resetSubmissionState);
        },
        [onPurchaseSubmit, planCode, currency, isSubmitting],
    );

    const handleGooglePayError = useCallback(
        (error: { code: string; message: string; cause?: unknown }) => {
            logger.error({ error }, 'Google Pay error');
            setGooglePayErrorMessage(error.message || 'An error occurred with Google Pay checkout');
        },
        [setGooglePayErrorMessage],
    );

    return (
        <div className="w-full space-y-8">
            {/* Express Checkout Section */}
            <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Express Checkout</h2>

                {isDigitalWalletsEnabled && (
                    <>
                        <div className="space-y-4">
                            {applePayErrorMessage && (
                                <ErrorBox
                                    message={applePayErrorMessage}
                                    onDismiss={() => setApplePayErrorMessage('')}
                                    className="mb-2"
                                />
                            )}
                            <ApplePayButton
                                isEnabled={isDigitalWalletsEnabled}
                                onSuccess={handleApplePaySuccess}
                                onError={handleApplePayError}
                                disabled={isSubmitting}
                            />
                        </div>
                        {googlePayErrorMessage && (
                            <ErrorBox message={googlePayErrorMessage} onDismiss={() => setGooglePayErrorMessage('')} />
                        )}
                        {GooglePayService.isAvailable() && total > 0 && (
                            <GooglePayCheckout
                                key={`google-pay-${total}-${currency}`}
                                onSuccess={handleGooglePaySuccess}
                                onError={handleGooglePayError}
                                currency={currency}
                                total={total.toFixed(2)}
                            />
                        )}
                    </>
                )}
                {paypalErrorMessage && (
                    <ErrorBox message={paypalErrorMessage} onDismiss={() => setPaypalErrorMessage('')} />
                )}
                <div className="space-y-3">
                    <PayPalCheckout
                        onSuccess={handlePayPalSuccess}
                        onError={handlePayPalError}
                        disabled={isSubmitting}
                    />
                </div>
            </div>

            {/* OR Divider */}
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-700 uppercase tracking-wider">OR</span>
                </div>
            </div>

            {/* Credit Card Form */}
            <form onSubmit={handleSubmit} ref={formRef} aria-label="Billing Form" className="w-full space-y-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">Credit Card Information</h2>
                    <p className="text-sm text-gray-600 mt-1">All fields are required.</p>
                </div>
                {formErrorMessage && (
                    <ErrorBox message={formErrorMessage} onDismiss={() => setFormErrorMessage('')} className="mb-6" />
                )}
                {/* Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ValidatedTextInput
                        label="First Name"
                        id="firstName"
                        dataRecurly="first_name"
                        placeholder="e.g. John"
                        autoComplete="given-name"
                        onChange={handleFirstNameUpdate}
                        errorMessage={firstNameErrorMessage}
                    />
                    <ValidatedTextInput
                        label="Last Name"
                        id="lastName"
                        dataRecurly="last_name"
                        placeholder="e.g. Smith"
                        autoComplete="family-name"
                        onChange={handleLastNameUpdate}
                        errorMessage={lastNameErrorMessage}
                    />
                </div>

                {/* Card Info */}
                <div>
                    <label htmlFor="cardNumber" className="block text-sm font-bold text-gray-900">
                        Card Number
                    </label>
                    <CardNumberElement
                        style={{
                            placeholder: {
                                content: '1234-1234-1234-1234',
                                color: '#626c78',
                            },
                        }}
                        className={cardNumberErrorMessage ? 'recurly-element-invalid' : ''}
                        id="cardNumber"
                        onChange={(state) => handleCardNumberUpdate(state)}
                    />
                    {cardNumberErrorMessage && (
                        <div className="text-red-600 text-sm font-semibold mt-1">{cardNumberErrorMessage}</div>
                    )}
                </div>

                <div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="expiryDate" className="block text-sm font-bold text-gray-900">
                                Expiry Date
                            </label>
                            <div id="expiryDate" className="grid grid-cols-2 gap-2">
                                <div>
                                    <CardMonthElement
                                        style={{
                                            placeholder: {
                                                content: 'MM',
                                                color: '#626c78',
                                            },
                                        }}
                                        className={cardMonthErrorMessage ? 'recurly-element-invalid' : ''}
                                        onChange={(state) => handleCardMonthUpdate(state)}
                                    />
                                    {cardMonthErrorMessage && (
                                        <div className="text-red-600 text-sm font-semibold mt-1">
                                            {cardMonthErrorMessage}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <CardYearElement
                                        style={{
                                            placeholder: {
                                                content: 'YY',
                                                color: '#626c78',
                                            },
                                        }}
                                        className={cardYearErrorMessage ? 'recurly-element-invalid' : ''}
                                        onChange={(state) => handleCardYearUpdate(state)}
                                    />
                                    {cardYearErrorMessage && (
                                        <div className="text-red-600 text-sm font-semibold mt-1">
                                            {cardYearErrorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <label htmlFor="cvc" className="block text-sm font-bold text-gray-900">
                                Security Code/CVV
                            </label>
                            <CardCvvElement
                                style={{
                                    placeholder: {
                                        content: '3 or 4 digit code',
                                        color: '#626c78',
                                    },
                                }}
                                className={cardCvvErrorMessage ? 'recurly-element-invalid' : ''}
                                onChange={(state) => handleCardCvvUpdate(state)}
                            />
                            {cardCvvErrorMessage && (
                                <div className="text-red-600 text-sm font-semibold mt-1">{cardCvvErrorMessage}</div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Billing Address */}
                <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-2 gap-4">
                        <ValidatedTextInput
                            label={postalCodeLabel}
                            id="postalCode"
                            dataRecurly="postal_code"
                            placeholder={postalCodePlaceholder}
                            autoComplete="postal-code"
                            errorMessage={postalCodeErrorMessage}
                            onChange={handleZipCodeUpdate}
                        />

                        <div>
                            <label htmlFor="country" className="block text-sm font-bold text-gray-900">
                                Country
                            </label>
                            <select
                                id="country"
                                name="country"
                                data-recurly="country"
                                autoComplete="country"
                                defaultValue=""
                                className="mt-1 block w-full border border-gray-700 rounded py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                onChange={handleCountryUpdate}
                            >
                                <option value="" className="text-gray-700">
                                    Select a Country
                                </option>
                                {getCountriesWithAbbreviations().map((country) => (
                                    <option key={country.abbreviation} value={country.abbreviation}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                            {countryErrorMessage && (
                                <div className="text-red-600 text-sm font-semibold mt-1">{countryErrorMessage}</div>
                            )}
                        </div>
                    </div>
                </div>

                {countryCode === 'BR' && (
                    <div>
                        <ValidatedTextInput
                            label="CPF/CNPJ"
                            id="cpf"
                            dataRecurly="tax_identifier"
                            placeholder="000.000.000-00 or 00.000.000/0000-00"
                            autoComplete="cpf"
                            onChange={handleCpfUpdate}
                            errorMessage={cpfErrorMessage}
                        />
                        {includeTaxIdentifierType && (
                            <input
                                type="hidden"
                                id="tax_identifier_type"
                                name="tax_identifier_type"
                                data-recurly="tax_identifier_type"
                                value="cpf"
                            />
                        )}
                    </div>
                )}
                {countryCode === 'GB' && (
                    <ValidatedTextInput
                        label="VAT ID"
                        id="vat"
                        dataRecurly="vat_number"
                        placeholder="GB123456789"
                        autoComplete="vat"
                        onChange={handleVatUpdate}
                        errorMessage={vatErrorMessage}
                    />
                )}

                <div className="w-full flex flex-col items-center space-y-4 text-sm">
                    <button
                        id="complete-payment-btn"
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full py-3 px-6 text-black font-semibold rounded focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:ring-offset-2 transition-colors ${
                            isSubmitting
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-yellow-400 hover:bg-yellow-500 cursor-pointer'
                        }`}
                    >
                        {isSubmitting ? 'Processing...' : 'Complete Payment'}
                    </button>
                    <p className="text-gray-700 flex items-center gap-1">
                        SSL Secure Payment
                        <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 1C8.676 1 6 3.676 6 7v2H5c-1.103 0-2 .897-2 2v8c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2v-8c0-1.103-.897-2-2-2h-1V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v2H8V7c0-2.276 1.724-4 4-4z" />
                            <circle cx="12" cy="14" r="1.5" fill="white" />
                        </svg>
                    </p>
                </div>
            </form>
        </div>
    );
}
